// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatComposer.tsx
 * @input Uses React, StyleX, BaseProps
 * @output Exports ChatComposer layout shell component
 * @position Core implementation; consumed by index.ts
 *
 * Layout shell for a chat composer. Arranges slots (drawer, header,
 * input, footer actions, send button, status) in a vertical stack with
 * page-radius container, hover/focus shadows, and concentric inner radius.
 *
 * Component CSS vars (themeable via defineTheme):
 * - `--_chat-composer-radius` (default: --radius-chat) — outer border radius
 * - `--_chat-composer-padding` (default: --spacing-3) — body padding
 * - Inner element radius = calc(--_chat-composer-radius - --_chat-composer-padding)
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Chat/Chat.doc.mjs
 * - /apps/storybook/stories/ChatComposer.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/ChatComposer/ (block examples)
 */
import React, { useState, useCallback, useRef, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, shadowVars, durationVars, easeVars, borderVars, typeScaleVars, typographyVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { Icon } from "../Icon/index.js";
import { ChatComposerInput } from "./ChatComposerInput.js";
import { ChatComposerContext } from "./ChatContext.js";
import { ChatSendButton } from "./ChatSendButton.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  body: {
    kVAEAm: "x1n2onr6",
    kY2c9j: "xhtitgo",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kmVPX3: "x18oj4ui",
    kOIVth: "x1txdalj",
    kaIpWk: "x1xunbrq",
    kWkggS: "x1prclbq",
    kkrTdU: "x1ed109x",
    kmkexE: "xrg7dcy",
    $$css: true
  },
  compact: {
    "--_chat-composer-padding": "x18zce08",
    kOIVth: "xzye2dw",
    $$css: true
  }
};

// Resting elevation for the composer body, narrowed to two steps.
// - `low` (default) preserves today's look: low at rest, bumping to med on
//   hover / focus-within (a CSS-only interaction state the component owns).
// - `none` is flat; depth comes from a border + inset rings instead. The
//   border is drawn *inside* the padding — its width is subtracted from every
//   side (like Card) so total inset (border + padding) equals the elevated
//   case and content geometry is unchanged. Border color, hover, and focus
//   states mirror TextInput (shared `inputWrapperStyles.base` treatment):
//   emphasized border at rest → accent on focus-within, with the same inset
//   shadow rings on hover and focus.
const elevationStyles = {
  low: {
    kGVxlE: "x1i5ehqx xhouywz",
    knuKkz: "xyavpck",
    $$css: true
  },
  none: {
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xvy26l8 x6q1khz",
    kmVPX3: "x1b50gpf",
    kGVxlE: "x1gnnqk1 x12y27fx xuihb5h",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * Layout shell for a chat composer with slots for drawer, input,
 * actions, and a send button.
 *
 * @example
 * ```
 * <ChatComposer
 *   onSubmit={(value) => console.log(value)}
 *   placeholder="Type a message..."
 * />
 * ```
 */
export function ChatComposer(props) {
  const t = useTranslator();
  const {
    ref,
    onSubmit,
    onStop,
    isStopShown = false,
    value: controlledValue,
    onChange,
    placeholder: placeholderFromProps,
    isDisabled = false,
    density = 'balanced',
    elevation = 'low',
    drawer,
    headerActions,
    headerContext,
    input,
    footerActions,
    sendActions,
    sendButton,
    status,
    statusPosition = 'bottom',
    xstyle,
    className,
    style,
    ...rest
  } = props;
  const placeholder = placeholderFromProps ?? t('@astryx.chat.composer.placeholder');
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;
  const updateValue = useCallback(newValue => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  }, [isControlled, onChange]);
  const handleSubmit = useCallback(() => {
    const trimmed = currentValue.trim();
    if (!trimmed || isDisabled) {
      return;
    }
    onSubmit(trimmed);
    updateValue('');
  }, [currentValue, isDisabled, onSubmit, updateValue]);
  const canSend = currentValue.trim().length > 0 && !isDisabled;
  const bodyRef = useRef(null);

  // Input slot registers its focus (and future) control here so the shell can
  // drive it without knowing its DOM shape. Stable ref — safe in the memoized
  // context value.
  const inputControlRef = useRef(null);
  const handleBodyClick = useCallback(e => {
    // Focus the input when clicking empty space in the body.
    // Skip if the click target is a button, link, or interactive element.
    const target = e.target;
    if (target.closest('button, a, [role="button"], [contenteditable="true"], [data-astryx-token]')) {
      return;
    }
    // Prefer the input's registered control (works for any input shape,
    // including editors whose focusable node isn't a bare
    // contenteditable/textarea). Fall back to a DOM query so uninstrumented
    // custom inputs still get click-to-focus.
    if (inputControlRef.current) {
      inputControlRef.current.focus();
      return;
    }
    const editable = bodyRef.current?.querySelector('[contenteditable="true"], textarea');
    editable?.focus();
  }, []);
  const statusEl = status ? /*#__PURE__*/_jsxs("div", {
    role: status.type === 'error' ? 'alert' : 'status',
    ...{
      0: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x1htvmrp xvpcij2 xckg3cf xkvxy4d x6k0y1c"
      },
      4: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x2gtn7z x9jihrk x1z0f2p2 xy7p8pm xzc3kxz"
      },
      2: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x1htvmrp xvpcij2 xckg3cf xkvxy4d x6k0y1c x1pritpl x1joocv1"
      },
      6: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x2gtn7z x9jihrk x1z0f2p2 xy7p8pm xzc3kxz x1pritpl x1joocv1"
      },
      1: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x1htvmrp xvpcij2 xckg3cf xkvxy4d x6k0y1c x24i8r5 xdhq94a"
      },
      5: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x2gtn7z x9jihrk x1z0f2p2 xy7p8pm xzc3kxz x24i8r5 xdhq94a"
      },
      3: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x1htvmrp xvpcij2 xckg3cf xkvxy4d x6k0y1c x24i8r5 xdhq94a"
      },
      7: {
        className: "x1n2onr6 x1ja2u2z x78zum5 x6s0dn4 x1txdalj x1pzlopt x141an7d x1ltkj2j x9ynric x2gtn7z x9jihrk x1z0f2p2 xy7p8pm xzc3kxz x24i8r5 xdhq94a"
      }
    }[!!(statusPosition === 'top') << 2 | !!(status.type === 'error') << 1 | !!(status.type === 'warning') << 0],
    children: [/*#__PURE__*/_jsx(Icon, {
      icon: status.type === 'error' ? 'error' : 'warning',
      size: "md",
      color: status.type === 'error' ? 'error' : 'warning'
    }), status.message]
  }) : null;
  const composerContext = useMemo(() => ({
    value: currentValue,
    onChange: updateValue,
    onSubmit: handleSubmit,
    placeholder,
    isDisabled,
    isStopShown,
    canSend,
    onStop,
    inputControlRef
  }), [currentValue, updateValue, handleSubmit, placeholder, isDisabled, isStopShown, canSend, onStop]);
  return /*#__PURE__*/_jsx(ChatComposerContext, {
    value: composerContext,
    children: /*#__PURE__*/_jsxs("div", {
      ref: ref,
      ...mergeProps(themeProps('chat-composer', {
        density
      }), {
        0: {
          className: "x1n2onr6 x1ja2u2z xc8icb0 x78zum5 xdt5ytf xx161y6 xk0ittl xcw7vlg"
        },
        1: {
          className: "x1n2onr6 x1ja2u2z xc8icb0 x78zum5 xdt5ytf xx161y6 xk0ittl xcw7vlg x197sbye x47corl"
        }
      }[!!isDisabled << 0], className, style),
      ...rest,
      children: [statusPosition === 'top' && statusEl, drawer, /*#__PURE__*/_jsxs("div", {
        ref: bodyRef,
        onClick: handleBodyClick,
        ...stylex.props(styles.body, elevationStyles[elevation], density === 'compact' && styles.compact, xstyle),
        children: [(headerActions || headerContext) && /*#__PURE__*/_jsxs("div", {
          ...{
            className: "x78zum5 x6s0dn4 x1qughib x1txdalj xe0p6wg"
          },
          children: [/*#__PURE__*/_jsx("div", {
            ...{
              className: "x78zum5 x6s0dn4 xzye2dw"
            },
            children: headerActions
          }), /*#__PURE__*/_jsx("div", {
            ...{
              className: "x78zum5 x6s0dn4 x1txdalj xvc5jky x141an7d x1ltkj2j xv1l7n4"
            },
            children: headerContext
          })]
        }), /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 xdt5ytf"
          },
          children: input ?? /*#__PURE__*/_jsx(ChatComposerInput, {})
        }), /*#__PURE__*/_jsxs("div", {
          ...{
            className: "x78zum5 x6s0dn4 x1qughib x1txdalj x21xpn4"
          },
          children: [/*#__PURE__*/_jsx("div", {
            ...{
              className: "x78zum5 x6s0dn4 xzye2dw"
            },
            children: footerActions
          }), /*#__PURE__*/_jsxs("div", {
            ...{
              className: "x78zum5 x6s0dn4 xzye2dw"
            },
            children: [sendActions, sendButton ?? /*#__PURE__*/_jsx(ChatSendButton, {})]
          })]
        })]
      }), statusPosition === 'bottom' && statusEl]
    })
  });
}
ChatComposer.displayName = 'ChatComposer';